import { useState } from 'react'

/**
 * Shareable invite (v0.20.0, D-120) — the Console side of `/i/:code`.
 *
 * Built because the only new relative to join arrived by word of mouth, never having
 * been emailed: for this family, a link you can paste into WhatsApp or a QR you can
 * hold up at a gathering beats another email.
 *
 * The QR encoder is DYNAMICALLY imported so it never lands in the main bundle, and
 * the code is rendered locally — the invite is never sent to a third-party QR service,
 * which would hand a working key to someone else's server.
 */
export default function ShareInvite({ code, treeName }: { code: string; treeName?: string }) {
  const [qr, setQr] = useState<string | null>(null)
  const [copied, setCopied] = useState<'link' | 'message' | null>(null)
  const link = `https://family-ancestry.istayintech.com/i/${code}`
  const message = `I've put our family tree in one place — ${treeName ? `“${treeName}”, ` : ''}everyone's on it and you're already in it.\n\nOpen this and sign in with your email:\n${link}`

  const copy = async (what: 'link' | 'message') => {
    try {
      await navigator.clipboard.writeText(what === 'link' ? link : message)
      setCopied(what); setTimeout(() => setCopied(null), 2000)
    } catch { /* clipboard blocked — the text is on screen to select */ }
  }

  const showQr = async () => {
    if (qr) { setQr(null); return }
    const QR = await import('qrcode')                       // separate chunk, loaded on demand
    setQr(await QR.toDataURL(link, { width: 220, margin: 1, color: { dark: '#0d0a1c', light: '#f5f2ea' } }))
  }

  return (
    <div className="mt-2 pt-2 border-t border-white/[0.06]">
      <div className="flex flex-wrap items-center gap-2">
        <code className="text-[11px] text-slate-400 bg-white/[0.04] rounded px-2 py-1 flex-1 min-w-[200px] truncate">{link}</code>
        <button onClick={() => copy('link')} className="text-xs px-2 py-1 rounded-lg border border-white/10 text-slate-300 hover:border-gold-500/40">
          {copied === 'link' ? '✓ copied' : 'Copy link'}
        </button>
        <button onClick={() => copy('message')} className="text-xs px-2 py-1 rounded-lg border border-white/10 text-slate-300 hover:border-gold-500/40">
          {copied === 'message' ? '✓ copied' : '💬 Copy message'}
        </button>
        <button onClick={showQr} className="text-xs px-2 py-1 rounded-lg border border-white/10 text-slate-300 hover:border-gold-500/40">
          {qr ? 'Hide QR' : 'QR'}
        </button>
      </div>
      {qr && (
        <div className="mt-3 flex items-center gap-3">
          <img src={qr} alt="Invite QR code" className="rounded-lg" width={130} height={130} />
          <p className="text-[11px] text-slate-500 max-w-[240px]">
            Hold this up at a family gathering. Anyone who scans it can join —
            so deactivate the code once everyone's in.
          </p>
        </div>
      )}
    </div>
  )
}
